import type { CSSProperties } from "react";

import { PLAY_ENTRY, PlayEntry, STAGE_VIEW, StageView } from "./constants";

/**
 * The stage's timelines. Each view plays the app's own move once, and every
 * moment of it is fixed here as a delay from the start of its play; the views
 * pass these to their CSS as custom properties, and CSS plays them. Each
 * view's play ends by the duration computed from the same moments, when the
 * provider marks it done.
 */

/** The deck dragged in and read, as Break Down an Initiative takes a file. */
export const PLAN_MOMENTS = {
  /** The deck appears outside the zone and starts toward it. */
  dragAt: 500,
  /** It is over the zone, which lights. */
  overAt: 1450,
  /** Dropped: the zone takes it and starts reading. */
  gotAt: 1750,
  /** Read. */
  readAt: 3050,
};

/** The deck starts moving this long after it appears. */
export const PLAN_DRAG_LAG_MS = 300;

/** The read check fades in over this long. */
export const PLAN_READ_FADE_MS = 300;

/** A card that arrives in reading order: each one this long after the one
    before it, from a short pause after the view shows. */
export const REVEAL = {
  startMs: 200,
  /** The room opened by drilling in fills once the reveal has run. */
  drillStartMs: 600,
  stepMs: 120,
  durationMs: 320,
};

/** The strategy graph's cards: the key result, the branch, the followed
    initiative, its three decisions, and the quiet initiative. */
const GRAPH_REVEALS = 7;

/** The room's participants (four) and then its options (three). */
export const ROOM_REVEALS = 7;

/** The newest decision lands in the list, then holds its highlight a moment. */
export const DECISIONS_MOMENTS = {
  landAt: 300,
  /** The row opens over this long; its text fades in after a short lag. */
  openMs: 500,
  showLagMs: 200,
  /** The highlight starts after the row begins to open, holds, then fades. */
  litLagMs: 300,
  litMs: 2400,
};

/** The weigh-ins are in: Converge completes and the room moves to Decide. */
export const ROOM_MOMENTS = {
  decideAt: 1500,
  /** The tracker's dots change over this long. */
  stageChangeMs: 300,
};

/** The end of a reveal of `count` cards starting at `startMs`. */
function revealEndMs(count: number, startMs: number): number {
  return startMs + (count - 1) * REVEAL.stepMs + REVEAL.durationMs;
}

/** How long each view's play runs. A view with no move is done as it shows.
    Each must reach the end of the last animation its view's CSS plays: when
    the play runs out the view drops its `playing` animations and shows its
    final state, so a CSS animation that outlasts its entry here is cut. */
export const PLAY_DURATION_MS: Record<StageView, number> = {
  [STAGE_VIEW.PLAN]: PLAN_MOMENTS.readAt + PLAN_READ_FADE_MS,
  [STAGE_VIEW.GRAPH]: revealEndMs(GRAPH_REVEALS, REVEAL.startMs),
  [STAGE_VIEW.DECISIONS]:
    DECISIONS_MOMENTS.landAt + DECISIONS_MOMENTS.litLagMs + DECISIONS_MOMENTS.litMs,
  [STAGE_VIEW.ROOM]: Math.max(
    ROOM_MOMENTS.decideAt + ROOM_MOMENTS.stageChangeMs,
    revealEndMs(ROOM_REVEALS, REVEAL.drillStartMs),
  ),
  [STAGE_VIEW.SIGNAL]: 0,
  [STAGE_VIEW.REVIEW]: 0,
};

/** Milliseconds as a CSS time. */
export function ms(value: number): string {
  return `${value}ms`;
}

/** The custom property a revealed card reads its place in the order from. */
export function revealOrder(order: number): CSSProperties {
  return { "--reveal-offset": ms(order * REVEAL.stepMs) } as CSSProperties;
}

/** The reveal's start and length, set on a view's frame for its cards. */
export function revealTiming(entry: PlayEntry | undefined): CSSProperties {
  const startMs = entry === PLAY_ENTRY.DRILL ? REVEAL.drillStartMs : REVEAL.startMs;

  return {
    "--reveal-start": ms(startMs),
    "--reveal-duration": ms(REVEAL.durationMs),
  } as CSSProperties;
}
